import { DAYS_SHORTNAMES_DICT, MONTH_DICT, MONTH_NAME_TO_NUM } from '@/consts';
import { withLeadingZero } from '../strings';

export function formatDate(date: Date, separator = '.', showYear = true) {
    return [withLeadingZero(date.getDate()), withLeadingZero(date.getMonth() + 1), showYear ? date.getFullYear() : null]
        .filter(Boolean)
        .join(separator);
}

/**
 *
 * @param startDate ex: 01.05.2025
 * @param endDate ex: 02.05.2025
 * @returns ex: 1-2 мая 2025
 */
export const formatDateRange = (startDate: string, endDate: string): string => {
    const start = startDate.split('.').map(Number) as [number, keyof typeof MONTH_DICT, number];
    const end = endDate.split('.').map(Number) as [number, keyof typeof MONTH_DICT, number];

    const isSameDay = start[0] === end[0];
    const isSameYear = start[2] === end[2];
    const isSameMonth = isSameYear && start[1] === end[1];

    if (isSameYear && isSameMonth && isSameDay) {
        return `${start[0]} ${MONTH_DICT[start[1]]} ${start[2]}`;
    } else if (isSameYear && isSameMonth) {
        return `${start[0]}-${end[0]} ${MONTH_DICT[start[1]]} ${start[2]}`;
    } else if (isSameYear && isSameDay) {
        return `${start[0]} ${MONTH_DICT[start[1]]} - ${MONTH_DICT[end[1]]} ${end[2]}`;
    } else if (isSameYear) {
        return `${start[0]} ${MONTH_DICT[start[1]]} - ${end[0]} ${MONTH_DICT[end[1]]} ${end[2]}`;
    } else {
        return `${start[0]} ${MONTH_DICT[start[1]]} ${start[2]} - ${end[0]} ${MONTH_DICT[end[1]]} ${end[2]}`;
    }
};

export const getDayOfWeekShortName = (date: Date) => {
    return DAYS_SHORTNAMES_DICT[date.getDay() as keyof typeof DAYS_SHORTNAMES_DICT];
};

export function formatLocalIso8601CalendarDate(date: Date): string | null {
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}

export function ddMmYyyyToIso8601Date(ddMmYyyy: string): string | null {
    const match = ddMmYyyy.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (!match) return null;
    const [, d, m, y] = match;
    const day = Number(d);
    const month = Number(m);
    const year = Number(y);
    if (!day || !month || !year || month > 12 || day > 31) return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const padDdMmPart = (n: string | number) => String(n).padStart(2, '0');

export const parseEventDates = (
    dateString: string,
    defaultYear: number = 2026,
): { startDay: string; endDay: string } | null => {
    const trimmed = dateString.trim();

    const rangeMatch = trimmed.match(/(\d+)\s*[–—-]\s*(\d+)\s+([а-яё]+)(?:\s+(\d{4}))?/i);
    if (rangeMatch) {
        const [, startDayRaw, endDayRaw, month, yearFromStr] = rangeMatch;
        const monthNum = MONTH_NAME_TO_NUM[month.toLowerCase()];
        if (monthNum === undefined) return null;
        const year = yearFromStr ? Number(yearFromStr) : defaultYear;
        return {
            startDay: `${padDdMmPart(startDayRaw)}.${padDdMmPart(monthNum)}.${year}`,
            endDay: `${padDdMmPart(endDayRaw)}.${padDdMmPart(monthNum)}.${year}`,
        };
    }

    const singleMatch = trimmed.match(/^(\d+)\s+([а-яё]+)(?:\s+(\d{4}))?$/i);
    if (singleMatch) {
        const [, day, month, yearFromStr] = singleMatch;
        const monthNum = MONTH_NAME_TO_NUM[month.toLowerCase()];
        if (monthNum === undefined) return null;
        const year = yearFromStr ? Number(yearFromStr) : defaultYear;
        const d = `${padDdMmPart(day)}.${padDdMmPart(monthNum)}.${year}`;
        return { startDay: d, endDay: d };
    }

    return null;
};
